import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GET_CUSTOM_MEASUREMENT_CONFIG_BY_ID,
  GET_MEASUREMENT_CONFIGS,
} from "../../queries/measuramentConfigQueries";

export function useMeasurementConfigs(digitalTwin = null) {
  const [measurementConfigsFrontEnd, setMeasurementConfigsFrontEnd] =
    useState(null);
  const [measurementConfigsBackEnd, setMeasurementConfigsBackEnd] =
    useState(null);

  const [
    customMeasurementConfigsFrontEnd,
    setCustomMeasurementConfigsFrontEnd,
  ] = useState(null);
  const [customMeasurementConfigsBackEnd, setCustomMeasurementConfigsBackEnd] =
    useState(null);

  // Regular measurement configs (unchanged)
  const {
    data: measurementConfigData,
    loading: loadingMeasurement,
    error: errorMeasurement,
  } = useQuery(GET_MEASUREMENT_CONFIGS, {
    fetchPolicy: "network-only",
    skip: !digitalTwin,
    nextFetchPolicy: "network-only",
  });

  // Debug log for regular measurement configs
  useEffect(() => {
    if (measurementConfigData) {
    }
    if (errorMeasurement) {
    }
  }, [measurementConfigData, errorMeasurement]);

  // Extract custom measurement IDs from digital twin
  const customMeasurementIds = useMemo(() => {
    if (!digitalTwin || !digitalTwin.custom_measurements) {
      return [];
    }

    const measurements = Array.isArray(digitalTwin.custom_measurements)
      ? digitalTwin.custom_measurements
      : [digitalTwin.custom_measurements];

    return measurements
      .map(measurement => {
        if (typeof measurement === "string") {
          return measurement;
        }
        return measurement._id || measurement.id;
      })
      .filter(Boolean);
  }, [digitalTwin]);

  // Use GET_CUSTOM_MEASUREMENT_CONFIG_BY_ID instead of GET_CUSTOM_MEASUREMENT_CONFIGS
  const {
    data: customMeasurementConfigData,
    loading: loadingCustomMeasurement,
    error: errorCustomMeasurement,
    refetch: refetchCustomMeasurementConfigs,
  } = useQuery(GET_CUSTOM_MEASUREMENT_CONFIG_BY_ID, {
    variables: { ids: customMeasurementIds },
    skip: customMeasurementIds.length === 0,
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
  });

  // Debug log for custom measurement configs
  useEffect(() => {
    if (customMeasurementConfigData) {
    }
    if (errorCustomMeasurement) {
    }
  }, [
    customMeasurementConfigData,
    errorCustomMeasurement,
    customMeasurementIds,
  ]);

  const configsByCategory = useMemo(() => {
    if (!measurementConfigData?.measurementConfigs) return {};

    return measurementConfigData.measurementConfigs.reduce((acc, config) => {
      if (!acc[config.category]) {
        acc[config.category] = [];
      }
      acc[config.category].push({
        name: config.fullName,
        fullName: config.fullName,
        description: config.description,
        shortName: config.shortName,
        defaultValue: "-",
        unit: config.unit,
        key: config.unique_identifier,
        redRegions: config.range?.red,
        yellowRegions: config.range?.yellow,
        greenRegions: config.range?.green,
        nature: config.nature,
        pythonClass: config.pythonClass,
      });
      return acc;
    }, {});
  }, [measurementConfigData]);

  const configDataByViewType = useMemo(() => {
    if (!measurementConfigData?.measurementConfigs) return {};

    return measurementConfigData.measurementConfigs.reduce((acc, config) => {
      const filteredConfig = {
        unique_identifier: config.unique_identifier,
        fullName: config.fullName,
        shortName: config.shortName,
        support: config.support,
        pythonClass: config.pythonClass,
        color: config.color,
      };

      config.viewtypes.forEach(viewType => {
        if (!acc[viewType]) {
          acc[viewType] = [];
        }
        acc[viewType].push(filteredConfig);
      });
      return acc;
    }, {});
  }, [measurementConfigData]);

  const customConfigsByCategory = useMemo(() => {
    // Change here: use customMeasurementConfigsByIds instead of customMeasurementConfigs
    if (!customMeasurementConfigData?.customMeasurementConfigsByIds) return {};

    return customMeasurementConfigData.customMeasurementConfigsByIds.reduce(
      (acc, config) => {
        if (!acc[config.category]) {
          acc[config.category] = [];
        }
        acc[config.category].push({
          _id: config._id,
          name: config.fullName,
          fullName: config.fullName,
          description: config.description,
          shortName: config.shortName,
          defaultValue: "-",
          unit: config.unit,
          key: config.unique_identifier,
          redRegions: config.range?.red,
          yellowRegions: config.range?.yellow,
          greenRegions: config.range?.green,
        });
        return acc;
      },
      {}
    );
  }, [customMeasurementConfigData]);

  const customConfigDataByViewType = useMemo(() => {
    // Change here: use customMeasurementConfigsByIds instead of customMeasurementConfigs
    if (!customMeasurementConfigData?.customMeasurementConfigsByIds) return {};

    return customMeasurementConfigData.customMeasurementConfigsByIds.reduce(
      (acc, config) => {
        const filteredConfig = {
          _id: config._id,
          unique_identifier: config.unique_identifier,
          fullName: config.fullName,
          shortName: config.shortName,
          support: config.support,
          pythonClass: config.pythonClass,
          color: config.color,
          category: config.category,
          viewtypes: config.viewtypes,
        };

        config.viewtypes.forEach(viewType => {
          if (!acc[viewType]) {
            acc[viewType] = [];
          }
          acc[viewType].push(filteredConfig);
        });
        return acc;
      },
      {}
    );
  }, [customMeasurementConfigData]);

  useEffect(() => {
    if (measurementConfigData?.measurementConfigs) {
      setMeasurementConfigsFrontEnd(configsByCategory);
      setMeasurementConfigsBackEnd(configDataByViewType);
    }
  }, [measurementConfigData, configsByCategory, configDataByViewType]);

  useEffect(() => {
    // Change here: use customMeasurementConfigsByIds
    if (customMeasurementConfigData?.customMeasurementConfigsByIds) {
      setCustomMeasurementConfigsFrontEnd(customConfigsByCategory);
      setCustomMeasurementConfigsBackEnd(customConfigDataByViewType);
    } else if (customMeasurementIds.length === 0) {
      // Clear configs if no custom measurements in digital twin
      setCustomMeasurementConfigsFrontEnd({});
      setCustomMeasurementConfigsBackEnd({});
    }
  }, [
    customMeasurementConfigData,
    customConfigsByCategory,
    customConfigDataByViewType,
    customMeasurementIds.length,
  ]);

  const refreshCustomMeasurementConfigs = useCallback(() => {
    if (customMeasurementIds.length > 0) {
      refetchCustomMeasurementConfigs();
    } else {
      // Clear configs if no custom measurements in digital twin
      setCustomMeasurementConfigsFrontEnd({});
      setCustomMeasurementConfigsBackEnd({});
    }
  }, [customMeasurementIds, refetchCustomMeasurementConfigs]);

  return {
    measurementConfigsFrontEnd,
    measurementConfigsBackEnd,
    customMeasurementConfigsFrontEnd,
    customMeasurementConfigsBackEnd,
    loadingMeasurement,
    errorMeasurement,
    loadingCustomMeasurement,
    errorCustomMeasurement,
    refreshCustomMeasurementConfigs,
  };
}
